import * as colors from '../../00colors.js';
import * as plugins from '../../00plugins.js';

import { demoFunc } from './dees-speechbubble.demo.js';
import {
  customElement,
  html,
  DeesElement,
  property,
  type TemplateResult,
  cssManager,
  css,
  type CSSResult,
  unsafeCSS,
  domtools,
  directives,
  unsafeHTML,
} from '@design.estate/dees-element';
import { DeesWindowLayer } from '../dees-windowlayer/dees-windowlayer.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-speechbubble': DeesSpeechbubble;
  }
}

@customElement('dees-speechbubble')
export class DeesSpeechbubble extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Overlay'];

  // STATIC
  public static async createAndShow(refElement: HTMLElement, textArg: string) {
    const windowLayer = await DeesWindowLayer.createAndShow({
      ownerElement: refElement,
      blur: false,
    });
    const speechbubble = document.createElement('dees-speechbubble');
    speechbubble.windowLayer = windowLayer;
    speechbubble.reffedElement = refElement;
    speechbubble.text = textArg;
    speechbubble.manifested = true;
    windowLayer.appendChild(speechbubble);
    windowLayer.style.pointerEvents = 'none';
    (windowLayer.shadowRoot!.querySelector('.windowOverlay') as HTMLElement).style.pointerEvents = 'none';
    return speechbubble;
  }

  private static hintId = 0;
  private hintTimer?: ReturnType<typeof setTimeout>;
  private hintListeners?: AbortController;

  /** Compact plain-text tooltip for the preceding control (or reffedElement). */
  @property({ type: Boolean, reflect: true })
  accessor hint = false;

  @property({ type: Boolean })
  accessor disabled = false;

  // INSTANCE
  @property({
    type: Object,
  })
  accessor reffedElement!: HTMLElement;

  @property({
    type: String,
    reflect: true,
  })
  accessor text!: string;

  @property({
    type: Boolean,
  })
  accessor wave: boolean = false;

  @property({
    type: Boolean,
  })
  accessor manifested = false;

  @property({
    type: String,
  })
  accessor status: 'normal' | 'pending' | 'success' | 'error' = 'normal';

  public windowLayer!: DeesWindowLayer;

  constructor() {
    super();
    this.registerStartupFunction(async signal => {
      await this.updateComplete;
      if (signal.aborted || !this.hint) return;
      const trigger = this.reffedElement ?? this.previousElementSibling as HTMLElement;
      if (!trigger) return;
      this.reffedElement = trigger;
      this.popover = 'manual';
      this.setAttribute('role', 'tooltip');
      this.id ||= `dees-speechbubble-hint-${++DeesSpeechbubble.hintId}`;
      this.updateHintDescription();
      trigger.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch' || this.disabled) return;
        this.clearHintTimer();
        this.hintTimer = setTimeout(() => this.showHint(), 250);
      }, { signal });
      trigger.addEventListener('pointerleave', () => this.hideHint(), { signal });
      trigger.addEventListener('focus', () => {
        if (trigger.matches(':focus-visible')) this.showHint();
      }, { signal });
      trigger.addEventListener('blur', () => this.hideHint(), { signal });
      trigger.addEventListener('pointerdown', () => this.hideHint(), { signal });
      trigger.addEventListener('click', () => this.hideHint(), { signal });
      signal.addEventListener('abort', () => {
        this.hideHint();
        const ids = (trigger.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(id => id && id !== this.id);
        if (ids.length) trigger.setAttribute('aria-describedby', ids.join(' '));
        else trigger.removeAttribute('aria-describedby');
      }, { once: true });
    });
  }

  public updated(changes: Map<string | number | symbol, unknown>) {
    super.updated(changes);
    if (this.hint) {
      this.updateHintDescription();
      if (this.disabled) this.hideHint();
    }
  }

  private updateHintDescription() {
    const trigger = this.reffedElement;
    if (!trigger || !this.id) return;
    const ids = new Set((trigger.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean));
    if (!this.disabled && this.text && this.text !== trigger.getAttribute('aria-label')) ids.add(this.id);
    else ids.delete(this.id);
    if (ids.size) trigger.setAttribute('aria-describedby', [...ids].join(' '));
    else trigger.removeAttribute('aria-describedby');
  }

  private clearHintTimer() {
    clearTimeout(this.hintTimer);
    this.hintTimer = undefined;
  }

  private showHint() {
    this.clearHintTimer();
    if (this.disabled || !this.isConnected || !this.reffedElement?.getClientRects().length || !this.text) return;
    this.showPopover();
    const anchor = this.reffedElement.getBoundingClientRect();
    const bubble = this.getBoundingClientRect();
    const onLeft = anchor.right + bubble.width + 8 > window.innerWidth - 8;
    this.toggleAttribute('hint-left', onLeft);
    const top = Math.max(8, Math.min(anchor.top + (anchor.height - bubble.height) / 2, window.innerHeight - bubble.height - 8));
    this.style.left = `${Math.max(8, onLeft ? anchor.left - bubble.width - 8 : anchor.right + 8)}px`;
    this.style.top = `${top}px`;
    this.style.setProperty('--hint-arrow-top', `${Math.max(8, Math.min(bubble.height - 8, anchor.top + anchor.height / 2 - top))}px`);
    this.hintListeners?.abort();
    this.hintListeners = new AbortController();
    const { signal } = this.hintListeners;
    document.addEventListener('keydown', event => { if (event.key === 'Escape') this.hideHint(); }, { capture: true, signal });
    document.addEventListener('scroll', () => this.hideHint(), { capture: true, signal });
    window.addEventListener('resize', () => this.hideHint(), { signal });
    window.addEventListener('blur', () => this.hideHint(), { signal });
  }

  private hideHint() {
    this.clearHintTimer();
    this.hintListeners?.abort();
    this.hintListeners = undefined;
    if (this.matches(':popover-open')) this.hidePopover();
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        --speechbubble-surface: var(--dees-material-regular-bg);
        --speechbubble-surface-opaque: var(--dees-material-regular-bg-opaque);
        --speechbubble-border: var(--dees-color-border-subtle);
        box-sizing: border-box;
        color: var(--dees-color-text-primary);
        user-select: none;
      }

      :host([hint]) {
        position: fixed;
        inset: auto;
        margin: 0;
        width: max-content;
        max-width: min(260px, calc(100vw - 16px));
        padding: 6px 10px;
        border: 0;
        border-radius: var(--dees-radius-md);
        background: var(--dees-color-tooltip-bg);
        color: var(--dees-color-tooltip-fg);
        box-shadow: var(--dees-shadow-sm);
        font: var(--dees-font-control-size-sm) / 1.4 var(--dees-font-family);
        overflow: visible;
        overflow-wrap: anywhere;
        pointer-events: none;
      }
      :host([hint]:not(:popover-open)) { display: none; }
      :host([hint])::before {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        left: -4px;
        top: var(--hint-arrow-top, 50%);
        transform: translateY(-50%) rotate(45deg);
        background: inherit;
        border: inherit;
        border-top: 0;
        border-right: 0;
      }
      :host([hint][hint-left])::before { left: auto; right: -4px; transform: translateY(-50%) rotate(225deg); }

      .maincontainer {
        position: relative;
        will-change: opacity, transform;
        transform: translateX(0px);
        margin-left: 0px;
        filter: drop-shadow(var(--dees-shadow-sm));
        pointer-events: none;
        opacity: 0;
        transition: opacity var(--dees-transition-fast) var(--dees-ease-standard), transform var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .arrow {
        position: absolute;
        transform: rotate(45deg);
        background: var(--speechbubble-surface);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        height: 15px;
        width: 15px;
        left: 2px;
        top: 12px;
        border-radius: var(--dees-radius-xs);
      }

      .speechbubble {
        background: var(--speechbubble-surface);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--speechbubble-border);
        padding: 0px 16px;
        border-radius: var(--dees-radius-lg);
        position: absolute;
        min-width: 240px;
        font-size: var(--dees-font-caption1-size);
        line-height: var(--dees-font-footnote-line);
        top: 0px;
        left: 8px;
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        :host {
          --speechbubble-surface: var(--speechbubble-surface-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        :host {
          --speechbubble-surface: var(--speechbubble-surface-opaque);
        }

        .arrow,
        .speechbubble {
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      .wave {
        animation-name: wave-animation; /* Refers to the name of your @keyframes element below */
        animation-duration: 2.5s; /* Change to speed up or slow down */
        animation-iteration-count: infinite; /* Never stop waving :) */
        transform-origin: 70% 70%; /* Pivot around the bottom-left palm */
        display: inline-block;
      }

      @keyframes wave-animation {
        0% {
          transform: rotate(0deg);
        }
        10% {
          transform: rotate(14deg);
        } /* The following five values can be played with to make the waving more or less extreme */
        20% {
          transform: rotate(-8deg);
        }
        30% {
          transform: rotate(14deg);
        }
        40% {
          transform: rotate(-4deg);
        }
        50% {
          transform: rotate(10deg);
        }
        60% {
          transform: rotate(0deg);
        } /* Reset for the last half to pause */
        100% {
          transform: rotate(0deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .maincontainer {
          transition: none;
        }

        .wave {
          animation: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    if (this.hint) return html`${this.text}`;
    return html`
      ${this.manifested
        ? html`
            <div class="maincontainer" @click=${this.handleClick}>
              <div class="arrow"></div>
              <div class="speechbubble">
                ${this.wave ? html`<span class="wave">👋</span>` : html``}
                ${directives.resolve(this.getHtml())}
              </div>
            </div>
          `
        : html``}
    `;
  }

  public async handleClick() {
    console.log('speechbubble got clicked.');
  }

  public async firstUpdated(changes: Map<string | number | symbol, unknown>) {
    super.firstUpdated(changes);
    if (this.hint) return;
    // lets make sure we have a ref
    if (!this.reffedElement) {
      this.reffedElement = this.previousElementSibling as HTMLElement;
    }
    if (this.manifested) {
      await this.updatePosition();
      (this.shadowRoot!.querySelector('.maincontainer') as HTMLElement).style.opacity = '1';
    } else {
      // lets make sure we instrument it
      let speechbubble: DeesSpeechbubble;
      this.reffedElement.addEventListener('mouseenter', async () => {
        speechbubble = await DeesSpeechbubble.createAndShow(this.reffedElement, this.text);
      });
      this.reffedElement.addEventListener('mouseleave', () => {
        speechbubble.destroy();
      });
    }
  }

  public async updatePosition() {
    const refElement = this.reffedElement;
    const boundingClientRect = refElement.getBoundingClientRect();
    this.style.position = 'fixed';
    this.style.top = `${boundingClientRect.top - 13}px`;
    this.style.left = `${boundingClientRect.left + refElement.clientWidth + 4}px`;
    if (boundingClientRect.right > 250) {
      this.style.width = `250px`;
    }
  }

  public async getHtml(): Promise<any> {
    if (!this.text) {
      return '';
    }
    const normalized = domtools.plugins.smartstring.normalize.standard(this.text);
    const result = await domtools.plugins.smartmarkdown.SmartMarkdown.easyMarkdownToHtml(
      normalized
    );
    return unsafeHTML(result);
  }

  public async show() {}

  public async destroy() {
    (this.shadowRoot!.querySelector('.maincontainer') as HTMLElement).style.opacity = '0';
    this.windowLayer.destroy();
  }
}
